import { createContext, useContext, useState, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useOnboardingSync, type WizardSummary } from '@/hooks/onboarding/useOnboardingSync';
import { useChecklistAutoComplete } from '@/hooks/onboarding/useChecklistAutoComplete';
import { useBusinessContext } from './BusinessContext';

interface OnboardingContextValue {
  onboardingMode: 'guided' | 'self' | null;
  appTourCompleted: boolean;
  completedSteps: Record<string, boolean>;
  tabToursSeen: Record<string, boolean>;
  checklistDismissed: boolean;
  wizardSummary: WizardSummary | null;
  onboardingConversationId: string | null;
  isLoading: boolean;

  setOnboardingMode: (mode: 'guided' | 'self') => void;
  startAppTour: () => void;
  completeAppTour: () => void;
  markStepCompleted: (key: string) => void;
  markTabTourSeen: (tabId: string) => void;
  dismissChecklist: () => void;
  startAIOnboarding: (conversationId: string) => void;

  isAppTourActive: boolean;
  shouldShowWelcome: boolean;
  shouldShowChecklist: boolean;
  checklistProgress: { completed: number; total: number };
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

// Checklist item keys (used for progress calculation)
const ALL_CHECKLIST_KEYS = [
  'review_form_created',
  'first_employee',
  'first_shift',
  'loyalty_program',
  'first_coupon',
  'try_ai_chat',
  'explore_analytics',
] as const;

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { selectedBusinessId } = useBusinessContext();
  const sync = useOnboardingSync();
  const [isAppTourActive, setIsAppTourActive] = useState(false);
  const [welcomeShown, setWelcomeShown] = useState(false);

  // Read wizard summary from sessionStorage on mount (set by CreateBusiness page)
  useEffect(() => {
    if (!selectedBusinessId || sync.isLoading) return;

    const storedSummary = sessionStorage.getItem('taginn_wizard_summary');
    if (storedSummary) {
      try {
        const summary = JSON.parse(storedSummary) as WizardSummary;
        sync.setWizardSummary(summary);
      } catch {
        // Invalid JSON, ignore
      }
      sessionStorage.removeItem('taginn_wizard_summary');
    }
  }, [selectedBusinessId, sync.isLoading]);

  // Determine if welcome should show
  const shouldShowWelcome = useMemo(() => {
    if (sync.isLoading) return false;
    // Show when arriving from create-business flow AND no mode chosen yet
    const hasWelcomeFlag = sessionStorage.getItem('taginn_show_welcome') === 'true';
    const noModeYet = !sync.data?.onboarding_mode;
    return hasWelcomeFlag && noModeYet && !welcomeShown;
  }, [sync.isLoading, sync.data?.onboarding_mode, welcomeShown]);

  const setOnboardingMode = useCallback((mode: 'guided' | 'self') => {
    sync.setOnboardingMode(mode);
    // Clear the welcome flag
    sessionStorage.removeItem('taginn_show_welcome');
    setWelcomeShown(true);
  }, [sync]);

  const startAppTour = useCallback(() => {
    setIsAppTourActive(true);
  }, []);

  const completeAppTour = useCallback(() => {
    setIsAppTourActive(false);
    sync.markAppTourCompleted();
  }, [sync]);

  const startAIOnboarding = useCallback((conversationId: string) => {
    // Single atomic DB write for both fields
    sync.upsertOnboarding({
      onboarding_mode: 'guided',
      onboarding_conversation_id: conversationId,
    });
    sessionStorage.removeItem('taginn_show_welcome');
    setWelcomeShown(true);
  }, [sync]);

  const shouldShowChecklist = useMemo(() => {
    if (sync.isLoading || !sync.data) return false;
    // Show when mode is set but checklist not dismissed
    return !!sync.data.onboarding_mode && !sync.data.checklist_dismissed;
  }, [sync.isLoading, sync.data]);

  const completedSteps = sync.data?.completed_steps ?? {};

  // Auto-complete checklist items based on user actions
  useChecklistAutoComplete({
    completedSteps,
    markStepCompleted: sync.markStepCompleted,
    onboardingMode: sync.data?.onboarding_mode ?? null,
  });

  const checklistProgress = useMemo(() => {
    const completed = ALL_CHECKLIST_KEYS.filter(k => completedSteps[k]).length;
    return { completed, total: ALL_CHECKLIST_KEYS.length };
  }, [completedSteps]);

  const contextValue = useMemo<OnboardingContextValue>(() => ({
    onboardingMode: sync.data?.onboarding_mode ?? null,
    appTourCompleted: sync.data?.app_tour_completed ?? false,
    completedSteps,
    tabToursSeen: sync.data?.tab_tours_seen ?? {},
    checklistDismissed: sync.data?.checklist_dismissed ?? false,
    wizardSummary: sync.data?.wizard_summary ?? null,
    onboardingConversationId: sync.data?.onboarding_conversation_id ?? null,
    isLoading: sync.isLoading,

    setOnboardingMode,
    startAppTour,
    completeAppTour,
    markStepCompleted: sync.markStepCompleted,
    markTabTourSeen: sync.markTabTourSeen,
    dismissChecklist: sync.dismissChecklist,
    startAIOnboarding,

    isAppTourActive,
    shouldShowWelcome,
    shouldShowChecklist,
    checklistProgress,
  }), [
    sync.data, sync.isLoading, completedSteps,
    setOnboardingMode, startAppTour, completeAppTour, startAIOnboarding,
    sync.markStepCompleted, sync.markTabTourSeen, sync.dismissChecklist,
    isAppTourActive, shouldShowWelcome, shouldShowChecklist, checklistProgress,
  ]);

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}

/** Safe version that returns null when outside the provider (e.g. landing page AI chat) */
export function useOnboardingSafe(): OnboardingContextValue | null {
  return useContext(OnboardingContext);
}
